import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthService } from '@core/auth/auth.service';
import { DataListRequest, DataListResponse } from '@shared/models/dataList.model';
import { GENERAL_ERROR_MESSAGE } from '@shared/models/messages';
import { SnackBarService } from '@shared/services/snack-bar.service';
import { parseErrorMessageFromBlob } from '@shared/utils/blob-error-message-parse.utils';
import { formatDate } from '@shared/utils/date-string-transform.utils';
import { formatDealSource, getKeysByDealSource } from '@shared/utils/deal-source-transform.utils';
import { catchError, EMPTY, map, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AssessmentsFilter } from '../models/assessment-filter.model';
import {
  Assessment,
  AssessmentCreation,
  AssessmentCreationRequest,
  AssessmentDetail,
  NewAssessmentResponse,
  RecalculateAssessmentResponse,
} from '../models/assessment.model';

@Injectable()
export class AssessmentsApiService {
  private readonly LH_ID = 14; // ID for "L&H" main line of business
  readonly http = inject(HttpClient);
  private readonly _messageService = inject(SnackBarService);
  private readonly _user = inject(AuthService).user;

  readonly url = `${environment.apiUrl}/assessments`;
  readonly urlV2 = `${environment.apiUrl}/v2/assessments`;

  getAssessments$(request: DataListRequest<AssessmentsFilter>): Observable<DataListResponse<Assessment>> {
    const params = {
      ...this.mapFilter(request.filter),
      page: request.page,
      ...(request.sort && { sort: request.sort }),
      ...(request.size && { size: request.size }),
    };
    return this.http.get<DataListResponse<Assessment>>(`${this.url}/filtered-assessments`, { params }).pipe(
      map(response => ({
        ...response,
        content: response.content.map(assessment => ({
          ...assessment,
          underwritingSource: formatDealSource(assessment.underwritingSource),
        })),
      })),
      catchError(error => {
        this._messageService.showError(`Failed to retrieve assessments:  ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return EMPTY;
      })
    );
  }

  exportAssessments$(filter: AssessmentsFilter): Observable<HttpResponse<Blob>> {
    const params = this.mapFilter(filter);
    return this.http.get(`${this.url}/export-filtered-assessments`, { params, responseType: 'blob', observe: 'response' }).pipe(
      catchError(error => {
        parseErrorMessageFromBlob(error).then(errorMessage =>
          this._messageService.showError(`Failed to export assessments: ${errorMessage}.`)
        );
        return EMPTY;
      })
    );
  }

  exportProtectedAreas$(id: number): Observable<HttpResponse<Blob>> {
    return this.http
      .get(`${this.url}/${id}/protected-areas/export`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        catchError(error => {
          this._messageService.showError(`Failed to export protected area coordinates: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
          return EMPTY;
        })
      );
  }

  mapFilter(filter: AssessmentsFilter): Record<string, string> {
    return Object.entries({
      country: filter.country.map(value => value.name).join(';'),
      countryIntersection: filter.countryIntersection ? 'true' : '',
      industry: filter.industry.join(';'),
      company: filter.company.join(';'),
      project: filter.project.join(';'),
      sensitivityLevel: filter.sensitivityLevel?.join(';'),
      dealId: filter.dealId?.join(';'),
      mainDriver: filter.mainDriver.join(';'),
      // transform sources back to ids
      underwritingSource: filter.underwritingSource.map(source => getKeysByDealSource(source))?.join(';'),
      createdBy: filter.createdById.map(value => value.userId).join(';'),
      dateFrom: formatDate(filter.dateFrom),
      dateTo: formatDate(filter.dateTo),
      businessUnit: filter.businessUnit?.join(';'),
      legalEntity: filter.legalEntity?.join(';'),
      userId: filter.userId,
      searchPhrase: filter.searchPhrase,
    }).reduce(
      (acc, [k, v]) => {
        if (v) acc[k] = v;
        return acc;
      },
      {} as Record<string, string>
    );
  }

  createAssessment$(request: AssessmentCreation): Observable<NewAssessmentResponse> {
    const body: AssessmentCreationRequest = {
      ...request,
      countries: request.countries.map(country => country.sdlId),
      company: request.company.sdlId,
      project: request.project.sdlId,
      industrySegment: request.sector.sdlId,
      mainLineOfBusinessId: request.isLH ? this.LH_ID : request.mainLineOfBusinessId,
      isThirdParty: request.isThirdParty,
      creatorId: this._user()?.userId ?? '',
    };

    return this.http.post<NewAssessmentResponse>(`${this.urlV2}`, body).pipe(
      catchError(error => {
        this._messageService.showError(`Failed to create assessment: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return throwError(() => error);
      })
    );
  }

  recalculateAssessment$(assessmentId: number): Observable<RecalculateAssessmentResponse> {
    return this.http.post<RecalculateAssessmentResponse>(`${this.urlV2}/${assessmentId}/retry`, null).pipe(
      catchError(error => {
        this._messageService.showError(`Failed to retry creating assessment: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return EMPTY;
      })
    );
  }

  getAssessmentDetailById$(assessmentId: number): Observable<AssessmentDetail> {
    return this.http.get<AssessmentDetail>(`${this.urlV2}/${assessmentId}`).pipe(
      map(assessment => ({
        ...assessment,
        dealSourceSystem: formatDealSource(assessment.dealSourceSystem),
      })),
      catchError(error => {
        this._messageService.showError(`Failed to retrieve assessment: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return EMPTY;
      })
    );
  }

  getAssessmentIndustryNames$(): Observable<string[]> {
    return this.http.get<string[]>(`${this.url}/industry-names`).pipe(
      catchError(error => {
        this._messageService.showError(`Failed to retrieve assessment sector names:  ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return EMPTY;
      })
    );
  }

  getDealIds$(): Observable<string[]> {
    return this.http.get<string[]>(`${this.url}/deal-ids`).pipe(
      catchError(error => {
        this._messageService.showError(`Failed to retrieve deal IDs: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return EMPTY;
      })
    );
  }

  getUnderwritingSources$(): Observable<string[]> {
    return this.http.get<string[]>(`${this.url}/underwriting-sources`).pipe(
      map(this.formatUniqueDealSources),
      catchError(error => {
        this._messageService.showError(`Failed to retrieve underwriting sources: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return EMPTY;
      })
    );
  }

  private formatUniqueDealSources(values: string[]): string[] {
    return Array.from(new Set(values.map(formatDealSource)));
  }

  getAssessmentCompanyNames$(): Observable<string[]> {
    return this.http.get<string[]>(`${this.url}/company-names`).pipe(
      catchError(error => {
        this._messageService.showError(`Failed to retrieve assessment company names: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return EMPTY;
      })
    );
  }

  getAssessmentProjectNames$(): Observable<string[]> {
    return this.http.get<string[]>(`${this.url}/project-names`).pipe(
      catchError(error => {
        this._messageService.showError(`Failed to retrieve assessment project names: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return EMPTY;
      })
    );
  }
}
